import { SpinnerSize } from '@amzn/stencil-react-components/spinner';
import React from 'react';
import { useContext, useEffect } from 'react';
import { Route, useLocation, useParams } from 'react-router-dom';
import GetUserDetails from 'src/builders/GetUserDetails';
import { ALLOW_LIST } from 'src/components/Authentication/AllowListedUsers';
import { UserContext } from 'src/components/Authentication/Authentication';
import ContentLoader from 'src/components/ContentLoader';
import { API_ENDPOINTS } from 'src/constants/Urls';
import { MAPLE_ONBOARDED_DOMAINS } from 'src/constants/enums';
import { useAsyncState } from 'src/hooks/useAsyncState';
import { User } from 'src/models/User';
import AccessDeniedPage from 'src/pages/AccessDeniedPage';
import { isTRDEnabledDomain } from 'src/pages/TRD/TRDUtils';
import { redirectToFailurePage } from 'src/utils/ErrorRedirectUtils';

const DomainAuthenticationComponent = (props) => {
  const userDetails = useAsyncState<User>();
  const { domainId }: { domainId: string } = useParams();
  const userContext = useContext(UserContext).authenticatedUser;
  const location = useLocation();
  const validateUserInDomain = async (employeeId, providedDomainId) => {
    try {
      userDetails.initiateAsyncState();
      if (!ALLOW_LIST.has(employeeId)) {
        const getUserDetailsResponse = await GetUserDetails.getUserDetails(
          providedDomainId,
          employeeId
        );
        userDetails.setAsyncSuccess(getUserDetailsResponse.user);
      } else {
        userDetails.setIsLoading(false);
      }
    } catch (error) {
      redirectToFailurePage(error, {
        source: 'DomainAuthenticatedRoutes - validateUserInDomain',
        api: API_ENDPOINTS.GET_USER_DETAILS
      });
      userDetails.setAsyncFailure(error);
    }
  };
  /**
   * This check helps prevent direct access to the TRD page via URL modification for domains that aren't enabled.
   */
  const validateTRDPath = () => {
    if (!isTRDEnabledDomain(domainId) && location.pathname.includes('/trd')) {
      redirectToFailurePage(undefined, {
        source: `Route guard - TRD not enabled for domain ${domainId}`
      });
    }
  };
  useEffect(() => {
    if (MAPLE_ONBOARDED_DOMAINS.includes(domainId)) {
      redirectToFailurePage(undefined, {
        source: `Route guard - domain ${domainId} is Maple-onboarded (managed in Maple)`
      });
    }
    validateTRDPath();
    void validateUserInDomain(userContext.employeeId, domainId).then();
  }, []);
  if (userDetails.isLoading) {
    return (
      <ContentLoader
        size={SpinnerSize.Large}
        loadingText={'Authenticating user'}
        fontSize={'T300'}
      />
    );
  }
  return ALLOW_LIST.has(userContext.employeeId) || userDetails.data ? (
    <Route>{props.children}</Route>
  ) : (
    <AccessDeniedPage />
  );
};
const DomainAuthenticatedRoutes = (props) => {
  return (
    <Route {...props}>
      <DomainAuthenticationComponent>
        {props.children}
      </DomainAuthenticationComponent>
    </Route>
  );
};

export default DomainAuthenticatedRoutes;
